import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { IonContent, IonPage } from "@ionic/react";
import { Row, Col, Form, Input, Button, Alert } from "antd";
import { MailOutlined, LockOutlined } from "@ant-design/icons";

import { useTranslation } from "react-i18next";
import metaData from "../data/metaData";
import permissionMetaData from "../data/permission";
import log from "../utils/log";
import c from "../utils/common";
import { showErrMsg } from "../utils/error";
import QRCode from "qrcode.react";
import speakeasy from "speakeasy";

//
import useAuth from "../hooks/useAuth";
import useOtp from "../hooks/useOtp";

//
import useSignUpStore from "../states/useSignUp";
import useAuthStore from "../states/useAuth";

const SignUpForm = ({ isLoadingEmailExist, onChangeEmail, onChangePassword, onNext }) => {
  const { t, i18n } = useTranslation();

  //Form
  const [form] = Form.useForm();

  const onFinish = async (values) => {
    onNext();
  };

  return (
    <Form form={form} name="register" onFinish={onFinish} scrollToFirstError>
      <Form.Item
        name="email"
        rules={[
          {
            type: "email",
            message: "The input is not valid E-mail!",
          },
          {
            required: true,
            message: "Please input your E-mail!",
          },
        ]}
      >
        <Input
          onChange={(e) => {
            onChangeEmail(e?.target?.value?.trim() || undefined);
          }}
          prefix={<MailOutlined />}
          placeholder="Email"
        />
      </Form.Item>

      <Form.Item
        name="password"
        rules={[
          {
            required: true,
            min: 9,
            max: 16,
            message: t("login.Pw_requirement"),
          },
          { pattern: /^\S*$/, message: t("register.rule_pw") },
        ]}
        hasFeedback
      >
        <Input.Password
          onChange={(e) => {
            onChangePassword(e?.target?.value?.trim() || undefined);
          }}
          prefix={<LockOutlined />}
          placeholder="Password"
        />
      </Form.Item>

      {/* <Form.Item
      name="agreement"
      valuePropName="checked"
      rules={[
        {
          validator: (_, value) =>
            value ? Promise.resolve() : Promise.reject(new Error('Should accept agreement')),
        },
      ]}
    >
      <Checkbox>
        I agree to the <a href="#">Terms of Service</a>, <a href="#">Privacy Policy</a>, and default <a href="#">Notification Settings</a>.
      </Checkbox>
    </Form.Item> */}

      <Form.Item>
        <Button
          loading={isLoadingEmailExist}
          type="primary"
          htmlType="submit"
          style={{ width: "100%", backgroundColor: "#1890ff" }}
        >
          Continue
        </Button>
      </Form.Item>
    </Form>
  );
};

const SignUp = ({ isLoadingEmailExist, onChangeEmail, onChangePassword, onNext }) => {
  const history = useHistory();
  return (
    <div style={{ width: "30vw", margin: "0 auto", padding: "20px" }}>
      <h1 style={{ fontSize: "2rem", color: "#001529", marginBottom: "24px", textAlign: "center" }}>
        Sign up to Pearl
      </h1>

      <SignUpForm
        isLoadingEmailExist={isLoadingEmailExist}
        onChangeEmail={onChangeEmail}
        onChangePassword={onChangePassword}
        onNext={onNext}
      />

      <div style={{ textAlign: "center", marginTop: "16px" }}>
        Already have an account?&nbsp;
        <a
          onClick={() => {
            history.push({
              pathname: "/login",
            });
          }}
        >
          Sign In
        </a>
      </div>
    </div>
  );
};

const Otp = ({ otp, onBack, onOtpCorrect, sendOTP }) => {
  const { OtpForm } = useOtp();

  return (
    <div style={{ width: "30vw", margin: "0 auto", padding: "20px" }}>
      <a
        style={{ fontSize: "1rem" }}
        onClick={() => {
          onBack();
        }}
      >
        Back
      </a>

      <OtpForm
        answer={otp}
        isLoading={false}
        onCorrect={() => {
          onOtpCorrect();
        }}
        sendOTP={() => {
          sendOTP();
        }}
      />
    </div>
  );
};

const TwoFa = ({ onBack }) => {
  const { t, i18n } = useTranslation();
  const history = useHistory();

  const [twoFaOtpForm] = Form.useForm();
  const [secret, setSecret] = useState();
  const [setUpURL, setSetUpURL] = useState();
  const [isShowTotpWrongMsg, setIsShowTotpWrongMsg] = useState(false);

  const [isLoadingAddTwoFa, setIsLoadingAddTwoFa] = useState(false);
  const { isResetTwoFa } = useAuthStore();
  const { newUser } = useSignUpStore();

  const { createAccount, addTwoFa } = useAuth();

  const onTwoFaOtpFormFinish = async () => {
    let isValid = await validateTotp(twoFaOtpForm.getFieldValue("totp"));
    log.debug(isValid);

    if (isValid) {
      if (isResetTwoFa) {
        _addTwoFa();
      } else {
        signUp();
      }
    } else {
      setIsLoadingAddTwoFa(false);
      setIsShowTotpWrongMsg(true);
    }
  };

  const getSecret = () => {
    let _secret = speakeasy.generateSecret({ length: 20 });
    setSecret(_secret);

    log.debug(_secret.base32);
    let str = "otpauth://totp/" + t("register.description_2") + "?secret=" + _secret.base32;
    log.debug(str);
    setSetUpURL(str);
  };

  const signUp = async () => {
    setIsLoadingAddTwoFa(true);

    if (newUser === undefined) {
      setIsLoadingAddTwoFa(false);
      return c.message({ type: "error", content: "Please fill in your user information before proceeding" });
    }

    let data = { ...newUser, secret: secret.ascii };
    log.debug(data);

    try {
      await createAccount(data);
      history.push({ pathname: "/get-started" });
    } catch (error) {
      showErrMsg(error);
    } finally {
      setIsLoadingAddTwoFa(false);
    }
  };

  const _addTwoFa = async () => {
    setIsLoadingAddTwoFa(true);
    try {
      await addTwoFa(secret.ascii);
      c.message({ type: "success", content: "Success" });
      history.push({ pathname: "/login" });
    } catch (error) {
      showErrMsg(error);
    } finally {
      setIsLoadingAddTwoFa(false);
    }
  };

  const onTwoFaOtpFormFinishFailed = (errorInfo) => {
    //log.debug('Failed:', errorInfo);
  };

  const validateTotp = async (totp) => {
    // account has no secret
    let isValid = speakeasy.totp.verify({
      secret: secret.ascii,
      encoding: "ascii",
      token: totp,
    });
    return isValid;
  };

  useEffect(() => {
    getSecret();
  }, []);

  return (
    <div style={{ width: "40vw", margin: "0 auto", padding: "20px" }}>
      <a
        style={{ fontSize: "1rem" }}
        onClick={() => {
          onBack();
        }}
      >
        Back
      </a>

      <Row>
        <h1 style={{ width: "100%", marginTop: "1rem", fontSize: "1.5rem" }}>Enable two-factor authentication (2FA)</h1>
        <p> Use a phone app like Goggle Authenticator to get 2FA codes when prompted during sign-in.</p>
        <p style={{ margin: 0 }}>
          <a
            onClick={() => {
              window.open(metaData.offical.GA.androidStore);
            }}
          >
            android download
          </a>
          <a
            style={{ marginLeft: "1rem" }}
            onClick={() => {
              window.open(metaData.offical.GA.appleStore);
            }}
          >
            iOS download
          </a>
        </p>

        <h2 style={{ width: "100%", marginTop: "2rem", marginBottom: "0.5rem", fontSize: "1.5rem" }}>
          Scan the QR code
        </h2>
        <p>
          Use Goggle Authenticator from your phone to scan.&nbsp;
          <a
            style={{ textDecoration: "underline" }}
            onClick={() => {
              history.push({ pathname: "/twoFa" });
            }}
          >
            learn more
          </a>
        </p>

        {secret ? (
          <Row style={{ width: "100%" }}>
            <QRCode
              style={{
                width: "10rem",
                height: "10rem",
                borderRadius: "1rem",
                border: "1px solid gray",
                padding: "0.75rem",
                marginBottom: "1rem",
              }}
              value={setUpURL}
            />
          </Row>
        ) : null}

        <Row style={{ width: "100%", fontSize: "0.75rem", color: "gray", fontSize: "1rem" }}>
          Enter the 6 digit under 「Pearl」
        </Row>

        <Form
          style={{
            margin: "1rem 0 5rem 0",
            width: "100%",
          }}
          form={twoFaOtpForm}
          onFinish={onTwoFaOtpFormFinish}
          onFinishFailed={onTwoFaOtpFormFinishFailed}
        >
          {isShowTotpWrongMsg == true ? (
            <Row style={{ marginBottom: "2vw" }}>
              <Col span={20}>
                <Alert
                  message={t("register.step3-2wrong")}
                  type="error"
                  closable
                  afterClose={() => {
                    setIsShowTotpWrongMsg(false);
                  }}
                />
              </Col>
            </Row>
          ) : null}

          <Form.Item
            name="totp"
            rules={[
              {
                required: true,
                min: 6,
                max: 6,
                message: t("register.step3-2req"),
              },
              {
                pattern: /^\d+$/,
                message: t("register.step3-2rule"),
              },
            ]}
            style={{ width: "100%" }}
          >
            <Input size="large" maxLength={6} style={{ width: "100%" }} />
          </Form.Item>

          <Button
            size="large"
            type="primary"
            loading={isLoadingAddTwoFa}
            onClick={() => {
              twoFaOtpForm.submit();
            }}
            style={{
              width: "10rem",
              marginTop: "2rem",
            }}
          >
            Continue
          </Button>
        </Form>
      </Row>
    </div>
  );
};

const SignUpPage = () => {
  const [step, setStep] = useState(1);
  const [isAnimating, setIsAnimating] = useState(false);

  const [email, setEmail] = useState();
  const [password, setPassword] = useState();
  const [isLoadingEmailExist, setIsLoadingEmailExist] = useState(false);

  const [otp, setOtp] = useState();
  const [isCoolDown, setIsCoolDown] = useState(false);

  const { isEmailExist } = useAuth();
  const { sendOTP } = useOtp();
  const { updateIsResetTwoFa } = useAuthStore();
  const { updateNewUser } = useSignUpStore();

  const _isEmailExist = async () => {
    setIsLoadingEmailExist(true);
    try {
      let exist = await isEmailExist(email);
      log.debug(exist);
      c.message({ type: "info", content: "Email already exist" });
      return exist;
    } catch (error) {
      //email not found, proceed
      if (error?.response?.data == "User not found") {
        return false;
      } else {
        showErrMsg(error);
        return true;
      }
      //else show error msg
    } finally {
      setIsLoadingEmailExist(false);
    }
  };

  const _sendOTP = async () => {
    //set cooldown
    setIsCoolDown(true);

    //only show message when clicked send again
    if (step === 2) {
      c.message({ type: "success", content: "OTP sent" });
    }

    //axios
    let _otp = await sendOTP(email);
    setOtp(_otp);
  };

  useEffect(() => {
    // enter page
    setIsAnimating(true);
    setTimeout(() => {
      setIsAnimating(false);
    }, 500);
  }, []);

  return (
    <IonPage>
      <IonContent className="ion-padding">
        <Row justify="center" align="middle" style={{ width: "100%", height: "100%" }}>
          <div className={`fade-container ${isAnimating ? "fade-out" : "fade-in"}`}>
            {step === 1 ? (
              <SignUp
                isLoadingEmailExist={isLoadingEmailExist}
                onChangeEmail={(value) => {
                  setEmail(value);
                }}
                onChangePassword={(value) => {
                  setPassword(value);
                }}
                onNext={async () => {
                  let exist = await _isEmailExist(email);
                  if (!exist) {
                    updateIsResetTwoFa(false);
                    await _sendOTP();
                    //before go to step 2
                    setIsAnimating(true);
                    setTimeout(() => {
                      setStep(2);
                      setIsAnimating(false);
                    }, 500);
                  }
                }}
              />
            ) : step === 2 ? (
              <Otp
                otp={otp}
                isCoolDown={isCoolDown}
                onBack={() => {
                  //before go back to step 1
                  setIsAnimating(true);
                  setTimeout(() => {
                    setStep(1);
                    setIsAnimating(false);
                  }, 500);
                }}
                onOtpCorrect={() => {
                  let newUser = {
                    email,
                    password,
                    permission: permissionMetaData.default.super,
                  };

                  updateNewUser("whole", newUser);

                  //before go to step 3
                  setIsAnimating(true);
                  setTimeout(() => {
                    setStep(3);
                    setIsAnimating(false);
                  }, 500);
                }}
                onFinishCoolDown={() => {
                  setIsCoolDown(false);
                }}
                sendOTP={() => {
                  _sendOTP();
                }}
              />
            ) : step === 3 ? (
              <TwoFa
                onBack={() => {
                  //before go back step 2
                  setIsAnimating(true); // Trigger animation
                  setTimeout(() => {
                    setStep(2);
                    setIsAnimating(false); // Animation done
                  }, 500); // Delay matches the animation duration in CSS
                }}
              />
            ) : null}
          </div>
        </Row>
      </IonContent>
    </IonPage>
  );
};

export default SignUpPage;
